import { useState, useEffect } from "react";
import { api, errMsg } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

function ScoreRow({ r, onSaved }) {
  const [s, setS] = useState(r.my_score || { teamwork: "", creativity: "", teaches: "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/judge/reels/${r.property_id}/score`, {
        teamwork: parseFloat(s.teamwork), creativity: parseFloat(s.creativity), teaches: parseFloat(s.teaches),
      });
      toast.success(`Scored ${r.property_name}`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`judge-row-${r.property_id}`}>
      <td className="px-3 py-2">
        <div className="font-medium text-sm text-[#1a1f36]">{r.property_name}</div>
        <div className="text-xs text-slate-500">{r.division} · {r.tier}</div>
      </td>
      <td className="px-3 py-2">
        {r.video_url ? <a href={r.video_url} target="_blank" rel="noreferrer" className="text-[#c8102e] text-sm hover:underline inline-flex items-center gap-1">Watch <ExternalLink className="w-3 h-3" /></a> : <span className="text-xs text-slate-400">No reel yet</span>}
      </td>
      {["teamwork", "creativity", "teaches"].map((k) => (
        <td key={k} className="px-2 py-2">
          <Input type="number" min="0" max="10" step="0.5" className="w-16 h-8" value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`judge-${k}-${r.property_id}`} />
        </td>
      ))}
      <td className="px-3 py-2 text-sm font-semibold">{r.avg != null ? `${r.avg}/30` : "—"}</td>
      <td className="px-3 py-2">
        <Button size="sm" disabled={busy || !r.video_url || s.teamwork === "" || s.creativity === "" || s.teaches === ""} onClick={save} data-testid={`judge-save-${r.property_id}`}>Save</Button>
      </td>
    </tr>
  );
}

export default function Judge() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/reels").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 fade-up">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#c8102e] mb-2">Judging panel</p>
      <h1 className="font-display text-3xl font-bold text-[#1a1f36] mb-2">Reel judging</h1>
      <p className="text-slate-600 mb-6 text-sm">Score each reel 0–10 on Teamwork on screen, Creativity, and Teaches something real. Three judges are averaged into the 30% Reel bucket.</p>
      <div className="border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-[#f8f9fc] text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-3 py-2">Property</th><th className="px-3 py-2">Reel</th><th className="px-2 py-2">Teamwork</th><th className="px-2 py-2">Creativity</th><th className="px-2 py-2">Teaches</th><th className="px-3 py-2">Avg</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => <ScoreRow key={r.property_id} r={r} onSaved={load} />)}
            {rows.length === 0 && <tr><td colSpan="7" className="px-3 py-10 text-center text-sm text-slate-500">No enrolled properties yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
